import { api } from "@/api/client";
import { PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { depthLabel, depthValue } from "@/lib/reasoningDepth";

const MONO = "font-family: var(--font-mono); font-size: 12px;";

export function ConfigView() {
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) => api.config(s),
    [],
    { intervalMs: 60_000 },
  );

  const onOff = (v: boolean): string => (v ? t("common.on") : t("common.off"));

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness
          lastFetched={lastFetched}
          loading={loading}
          error={error}
          onRefresh={refetch}
        />
      </div>

      <section class="card" style="padding-bottom: var(--space-3);">
        <PanelHelp k="config.overview" />
      </section>

      <section class="card" style="border-left: 4px solid var(--brand-orange);">
        <h2>{t("config.edge")}</h2>
        <PanelHelp k="config.edge" />
        <table class="table">
          <tbody>
            {data && !data.edge_protection && (
              <tr>
                <td colspan={2} class="muted">
                  {t("config.edgeNotReported")}
                </td>
              </tr>
            )}
            {data?.edge_protection && !data.edge_protection.configured && (
              <tr>
                <td colspan={2} class="muted">
                  {t("config.edgeNotConfigured")}
                </td>
              </tr>
            )}
            {data?.edge_protection?.configured && (
              <>
                <Row k={t("config.row.policy")} v={data.edge_protection.policy_name || "—"} />
                <Row
                  k={t("config.row.allowedEdge")}
                  v={data.edge_protection.allowed_countries.join(", ")}
                />
                <Row
                  k={t("config.row.everythingElse")}
                  v={
                    data.edge_protection.default_action === "deny_403"
                      ? t("config.blockedAtEdge")
                      : data.edge_protection.default_action
                  }
                />
                <Row
                  k={t("config.row.rateLimit")}
                  v={
                    data.edge_protection.rate_limit_enabled
                      ? t("config.rateLimitOn", {
                          n: data.edge_protection.rate_limit_per_minute ?? "?",
                        })
                      : t("common.off")
                  }
                />
                <Row
                  k={t("config.row.botProbe")}
                  v={
                    data.edge_protection.bot_probe_block_enabled
                      ? t("config.botProbeOn")
                      : t("common.off")
                  }
                />
                <Row
                  k={t("config.row.blockedSee")}
                  v={
                    data.edge_protection.widget_assets_public
                      ? t("config.blockedSeeNotice")
                      : t("config.blockedSeeNothing")
                  }
                />
                <Row
                  k={t("config.row.appBackstop")}
                  v={data.edge_protection.app_allowed_countries.join(", ")}
                />
                <Row k={t("config.row.listsMatch")} v={data.edge_protection.countries_match_app} />
              </>
            )}
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>{t("config.modes")}</h2>
        <PanelHelp k="config.modes" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("config.col.mode")}</th>
              <th>{t("config.col.model")}</th>
              <th>{t("config.col.provider")}</th>
              <th>{t("config.col.depth")}</th>
              <th>{t("config.col.enabled")}</th>
            </tr>
          </thead>
          <tbody>
            {data?.modes.map((m) => (
              <tr key={m.mode}>
                <td>{m.mode}</td>
                <td style={MONO}>{m.model}</td>
                <td style={MONO}>{m.provider || "—"}</td>
                {/* Named in the provider's own vocabulary, as on the Prompts
                    view: Claude effort vs. Gemini thinking. */}
                <td>
                  <span class="muted">{depthLabel(m)}</span>{" "}
                  <span style={MONO}>{depthValue(m)}</span>
                </td>
                <td>
                  <span class={`pill ${m.enabled ? "ok" : "down"}`}>{onOff(m.enabled)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>{t("config.flags")}</h2>
        <PanelHelp k="config.featureFlags" />
        <table class="table">
          <tbody>
            <Row k={t("config.flag.adminEnabled")} v={data?.admin_enabled} />
            <Row k={t("config.flag.adminToken")} v={data?.admin_token_set} />
            <Row k={t("config.flag.multicheck")} v={data?.multicheck_enabled} />
            <Row k={t("config.flag.modelArmor")} v={data?.model_armor_enabled} />
            <Row k={t("config.flag.reranker")} v={data?.reranker_enabled} />
            <Row k={t("config.flag.tracing")} v={data?.tracing_enabled} />
            <Row k={t("config.flag.tracingRate")} v={data?.tracing_sample_rate?.toFixed(2)} />
            <Row k={t("config.flag.metrics")} v={data?.metrics_enabled} />
            <Row k={t("config.flag.testKeyword")} v={data?.test_session_keyword_set} />
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>{t("config.limits")}</h2>
        <PanelHelp k="config.limits" />
        <table class="table">
          <tbody>
            <Row k={t("config.limit.project")} v={data?.gcp_project_id} />
            <Row k={t("config.limit.region")} v={data?.gcp_region} />
            <Row k={t("config.limit.firestore")} v={data?.firestore_database} />
            <Row k={t("config.limit.promptVersion")} v={data?.prompt_version} />
            <Row k={t("config.limit.allowedCountries")} v={data?.allowed_countries} />
            <Row k={t("config.limit.anonLimit")} v={data?.anonymous_question_limit} />
            <Row k={t("config.limit.rateSession")} v={data?.rate_limit_per_session} />
            <Row k={t("config.limit.rateIp")} v={data?.rate_limit_per_ip} />
            <Row k={t("config.limit.historyTurns")} v={data?.history_max_turns} />
            <Row k={t("config.limit.historyTokens")} v={data?.history_soft_max_tokens ?? "—"} />
            <Row k={t("config.limit.ttl")} v={data?.session_ttl_days_anonymous} />
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>{t("config.knobs")}</h2>
        <table class="table">
          <tbody>
            <Row k={t("config.knob.recent")} v={data?.admin_dashboard.recent_sessions_limit} />
            <Row k={t("config.knob.maxWindow")} v={data?.admin_dashboard.max_window_days} />
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Row({ k, v }: { k: string; v: unknown }) {
  return (
    <tr>
      <td class="muted" style="width: 280px;">{k}</td>
      <td style="font-family: var(--font-mono); font-size: 13px;">
        {v === undefined || v === null ? (
          <span class="muted">—</span>
        ) : typeof v === "boolean" ? (
          v ? (
            <span class="pill ok">{t("common.yes")}</span>
          ) : (
            <span class="pill down">{t("common.no")}</span>
          )
        ) : (
          String(v)
        )}
      </td>
    </tr>
  );
}
